import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { ArrowLeft, Check, Zap, Crown, Loader2, Sparkles, Infinity, Brain } from "lucide-react";
import FuturaIcon from "../components/FuturaIcon";
import { useAuth, API } from "../App";
import { useLanguage } from "../i18n/LanguageContext";
import { LanguageSelector } from "../components/LanguageSelector";
import { useIsWebView } from "../hooks/useIsWebView";
import axios from "axios";

const Pricing = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t } = useLanguage();
  const [checkoutError, setCheckoutError] = useState("");
  const isWebView = useIsWebView();
  const [loadingPlan, setLoadingPlan] = useState(null);
  const [billingPeriod, setBillingPeriod] = useState("monthly");
  const [showCancelModal, setShowCancelModal] = useState(false);

  // Double-guard: If somehow reached in WebView, redirect immediately
  if (isWebView) {
    navigate("/dashboard", { replace: true });
    return null;
  }

  const plans = [
    {
      id: "descubre", name: t("plan_descubre"), badge: t("plan_descubre_badge"),
      badgeColor: "bg-emerald-500/10 text-emerald-400 border-emerald-500/20",
      priceMonthly: "0", priceAnnual: "0", savings: null,
      description: t("plan_descubre_desc"),
      emotionTag: t("plan_descubre_emotion"),
      features: [t("pf_1sim"), t("pf_ai_rec"), t("pf_basic_motives"), t("pf_avatar"), t("pf_checkin_free")],
      missing: [t("pmiss_smart_ai"), t("pmiss_history"), t("pmiss_comparator"), t("pmiss_premium"), t("pmiss_library"), t("pmiss_diary_ai")],
      icon: FuturaIcon, popular: false, gradient: "from-white/5 to-white/[0.02]", accentColor: "emerald"
    },
    {
      id: "decide", name: t("plan_decide"), badge: t("plan_decide_badge"),
      badgeColor: "bg-blue-500/10 text-blue-400 border-blue-500/20",
      priceMonthly: "5,99", priceAnnual: "57,50", savings: `${t("plan_saves")} 14,38`,
      description: t("plan_decide_desc"),
      emotionTag: t("plan_decide_emotion"),
      emotionHook: t("plan_decide_hook"),
      features: [t("pf_50sim"), t("pf_smart_ai"), t("pf_detailed_motives"), t("pf_full_history"), t("pf_favorites"), t("pf_avatar"), t("pf_avatar_challenges"), t("pf_avatar_levels")],
      moduleTitle: t("pricing_module1_title"),
      moduleFeatures: [t("pf_audio_guides"), t("pf_routines"), t("pf_smart_recs")],
      missing: [t("pmiss_comparator"), t("pmiss_premium"), t("pmiss_emotional"), t("pmiss_diary_ai")],
      icon: Zap, popular: false, gradient: "from-blue-500/10 to-blue-500/[0.02]", accentColor: "blue"
    },
    {
      id: "avanza", name: t("plan_avanza"), badge: t("plan_avanza_badge"),
      badgeColor: "bg-amber-500/10 text-amber-400 border-amber-500/20",
      priceMonthly: "8,99", priceAnnual: "86,30", savings: `${t("plan_saves")} 21,58`,
      description: t("plan_avanza_desc"),
      emotionTag: t("plan_avanza_emotion"),
      emotionHook: t("plan_avanza_hook"),
      features: [t("pf_unlimited"), t("pf_premium_ai"), t("pf_full_history"), t("pf_comparator"), t("pf_premium_report"), t("pf_top3"), t("pf_confidence"), t("pf_action"), t("pf_favorites"), t("pf_avatar"), t("pf_avatar_challenges"), t("pf_avatar_levels")],
      moduleTitle: t("pricing_module1_title"),
      moduleFeatures: [t("pf_audio_guides"), t("pf_routines"), t("pf_smart_recs")],
      module2Title: t("pricing_module2_title"),
      module2Features: [t("pf_checkin"), t("pf_mood_tracker"), t("pf_weekly_charts"), t("pf_ai_insights"), t("pf_patterns"), t("pf_diary_ai_desc")],
      missing: [], icon: Crown, popular: true, gradient: "from-amber-500/10 to-amber-500/[0.02]", accentColor: "amber"
    }
  ];

  const handleSubscribe = async (planId) => {
    if (!user) { navigate("/register"); return; }
    if (planId === "descubre") return;
    setLoadingPlan(planId);
    try {
      const response = await axios.post(`${API}/payments/checkout`, {
        package_id: planId, origin_url: window.location.origin, billing_period: billingPeriod
      }, { withCredentials: true, timeout: 30000 });
      if (response.data.url) window.location.href = response.data.url;
    } catch (error) {
      setCheckoutError(error.response?.data?.detail || "Error al procesar el pago");
      setTimeout(() => setCheckoutError(""), 5000);
    } finally { setLoadingPlan(null); }
  };

  const currentPlan = user?.plan || "descubre";

  return (
    <div className="min-h-screen bg-[#0a0a0f] text-white" data-testid="pricing-page">
      <div className="fixed inset-0 bg-gradient-to-br from-[#0a0a0f] via-[#0d1020] to-[#0a0a0f] pointer-events-none" />

      <header className="relative z-20 border-b border-white/5">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <Link to={user ? "/dashboard" : "/"} className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-xl bg-gradient-to-br from-[#002FA7] to-[#5B21B6] flex items-center justify-center p-1">
              <FuturaIcon className="w-full h-full text-white" />
            </div>
            <span className="font-['Outfit'] font-semibold text-lg">FUTURA</span>
          </Link>
          <div className="flex items-center gap-3">
            <LanguageSelector variant="dark" />
            <Button variant="ghost" onClick={() => navigate(-1)} className="text-white/60 hover:text-white" data-testid="pricing-back-btn">
              <ArrowLeft className="w-4 h-4 mr-2" />{t("nav_back")}
            </Button>
          </div>
        </div>
      </header>

      <main className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 py-10 sm:py-20">
        {checkoutError && (
          <div className="mb-6 p-4 bg-red-500/10 border border-red-500/20 rounded-2xl text-center animate-fade-in" data-testid="checkout-error">
            <p className="text-red-400 text-sm">{checkoutError}</p>
          </div>
        )}
        <div className="text-center mb-10 sm:mb-14">
          <h1 className="font-['Outfit'] text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight mb-4">{t("pricing_title")}</h1>
          <p className="text-base sm:text-lg text-white/50 max-w-xl mx-auto mb-8">{t("pricing_subtitle")}</p>

          <div className="inline-flex items-center bg-white/5 border border-white/10 rounded-full p-1" data-testid="billing-toggle">
            <button onClick={() => setBillingPeriod("monthly")} className={`px-5 py-2 rounded-full text-sm font-medium transition-all ${billingPeriod === "monthly" ? "bg-white/10 text-white" : "text-white/50 hover:text-white/70"}`} data-testid="toggle-monthly">
              {t("pricing_monthly")}
            </button>
            <button onClick={() => setBillingPeriod("annual")} className={`px-5 py-2 rounded-full text-sm font-medium transition-all flex items-center gap-2 ${billingPeriod === "annual" ? "bg-white/10 text-white" : "text-white/50 hover:text-white/70"}`} data-testid="toggle-annual">
              {t("pricing_annual")}
              <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-500/20 text-emerald-400 border border-emerald-500/30">-20%</span>
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 sm:gap-6 max-w-5xl mx-auto">
          {plans.map((plan) => {
            const isCurrentPlan = currentPlan === plan.id;
            const Icon = plan.icon;
            const price = billingPeriod === "annual" && plan.id !== "descubre" ? plan.priceAnnual : plan.priceMonthly;
            let periodLabel = t("pricing_month");
            if (plan.id === "descubre") periodLabel = t("pricing_forever");
            else if (billingPeriod === "annual") periodLabel = t("pricing_year");

            return (
              <div key={plan.id} className={`relative bg-gradient-to-b ${plan.gradient} border rounded-3xl p-6 sm:p-8 transition-all duration-300 flex flex-col ${plan.popular ? "border-amber-500/40 shadow-lg shadow-amber-500/10 md:scale-[1.02]" : "border-white/10 hover:border-white/20"}`} data-testid={`plan-${plan.id}`}>
                <div className="flex items-center justify-between mb-4">
                  <span className={`text-xs font-medium px-3 py-1 rounded-full border ${plan.badgeColor}`}>{plan.badge}</span>
                  <Icon className="w-5 h-5 text-white/40" />
                </div>
                <h3 className="font-['Outfit'] text-2xl font-semibold mb-1">{plan.name}</h3>
                <div className="flex items-baseline gap-1 mb-1">
                  <span className="text-4xl font-bold">{price}&euro;</span>
                  <span className="text-white/40 text-sm">{periodLabel}</span>
                </div>
                {billingPeriod === "annual" && plan.savings && (
                  <p className="text-emerald-400 text-xs font-medium mb-2" data-testid={`savings-${plan.id}`}>{plan.savings}&euro;</p>
                )}
                {plan.id !== "descubre" && (
                  <p className="text-white/30 text-xs mb-3">{t("pricing_cancel_anytime")}</p>
                )}

                {/* Emotional hook */}
                {plan.emotionTag && (
                  <p className="text-sm font-medium mb-2" style={{ color: plan.accentColor === "amber" ? "#f59e0b" : plan.accentColor === "blue" ? "#3b82f6" : "#10b981" }}>
                    {plan.emotionTag}
                  </p>
                )}
                <p className="text-white/50 text-sm mb-3">{plan.description}</p>
                {plan.emotionHook && (
                  <p className="text-white/40 text-xs italic mb-6 leading-relaxed border-l-2 pl-3" style={{ borderColor: plan.accentColor === "amber" ? "#f59e0b33" : "#3b82f633" }}>
                    {plan.emotionHook}
                  </p>
                )}
                {!plan.emotionHook && <div className="mb-6" />}

                {!isWebView ? (
                  <Button onClick={() => handleSubscribe(plan.id)} disabled={isCurrentPlan || loadingPlan === plan.id} className={`w-full rounded-full py-6 font-medium text-base mb-8 ${plan.popular ? "bg-gradient-to-r from-amber-500 to-orange-500 hover:opacity-90 text-black" : isCurrentPlan ? "bg-white/10 text-white/50 cursor-default" : "bg-white/10 hover:bg-white/20 text-white"}`} data-testid={`subscribe-${plan.id}`}>
                    {loadingPlan === plan.id && <Loader2 className="w-5 h-5 animate-spin" />}
                    {loadingPlan !== plan.id && isCurrentPlan && t("pricing_your_plan")}
                    {loadingPlan !== plan.id && !isCurrentPlan && plan.id === "descubre" && t("pricing_free")}
                    {loadingPlan !== plan.id && !isCurrentPlan && plan.id !== "descubre" && t("pricing_subscribe")}
                  </Button>
                ) : (
                  <div className="mb-8">
                    {isCurrentPlan && (
                      <div className="w-full text-center py-3 rounded-full bg-white/10 text-white/50 text-base font-medium">{t("pricing_your_plan")}</div>
                    )}
                  </div>
                )}

                <div className="space-y-3 flex-1">
                  {plan.features.map((f) => (
                    <div key={f} className="flex items-start gap-3"><Check className="w-4 h-4 text-emerald-400 mt-0.5 flex-shrink-0" /><span className="text-sm text-white/70">{f}</span></div>
                  ))}
                  {plan.moduleTitle && (
                    <div className="pt-3 mt-3 border-t border-white/10">
                      <p className="text-xs font-semibold text-[#10B981] uppercase tracking-wide mb-2">{plan.moduleTitle}</p>
                      {plan.moduleFeatures.map((f) => (
                        <div key={f} className="flex items-start gap-3 mb-2"><Check className="w-4 h-4 text-[#10B981] mt-0.5 flex-shrink-0" /><span className="text-sm text-white/60">{f}</span></div>
                      ))}
                    </div>
                  )}
                  {plan.module2Title && (
                    <div className="pt-3 mt-3 border-t border-white/10">
                      <div className="flex items-center gap-2 mb-2">
                        <Sparkles className="w-3.5 h-3.5 text-amber-400" />
                        <p className="text-xs font-semibold text-amber-400 uppercase tracking-wide">{plan.module2Title}</p>
                      </div>
                      {plan.module2Features.map((f) => (
                        <div key={f} className="flex items-start gap-3 mb-2"><Check className="w-4 h-4 text-amber-400 mt-0.5 flex-shrink-0" /><span className="text-sm text-white/60">{f}</span></div>
                      ))}
                    </div>
                  )}
                  {plan.missing.map((f) => (
                    <div key={f} className="flex items-start gap-3 opacity-40"><div className="w-4 h-4 mt-0.5 flex-shrink-0 border border-white/20 rounded" /><span className="text-sm text-white/40 line-through">{f}</span></div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>

        {/* Diario IA highlight */}
        <div className="max-w-4xl mx-auto mt-16 bg-gradient-to-r from-amber-500/5 to-[#5B21B6]/5 border border-amber-500/20 rounded-3xl p-8 sm:p-12 text-center" data-testid="diary-highlight-pricing">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-amber-500/10 text-amber-400 text-xs font-semibold uppercase tracking-wide mb-4">
            <Sparkles className="w-3.5 h-3.5" />
            {t("landing_diary_badge")}
          </div>
          <h3 className="font-['Outfit'] text-2xl sm:text-3xl font-semibold mb-3">{t("landing_diary_title")}</h3>
          <p className="text-white/50 text-sm sm:text-base max-w-2xl mx-auto mb-6">{t("landing_diary_subtitle")}</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-w-xl mx-auto text-left mb-8">
            {[t("landing_diary_f1"), t("landing_diary_f2"), t("landing_diary_f3"), t("landing_diary_f4")].map((f) => (
              <div key={f} className="flex items-start gap-2 text-sm text-white/60">
                <Sparkles className="w-3.5 h-3.5 text-amber-400 mt-0.5 flex-shrink-0" />
                {f}
              </div>
            ))}
          </div>
          {currentPlan !== "avanza" && !isWebView && (
            <Button onClick={() => handleSubscribe("avanza")} className="bg-gradient-to-r from-amber-500 to-orange-500 hover:opacity-90 text-black font-semibold rounded-full px-8 py-5" data-testid="diary-cta-pricing">
              {t("landing_diary_cta")} <ArrowLeft className="w-4 h-4 ml-2 rotate-180" />
            </Button>
          )}
        </div>

        <div className="text-center mt-14 space-y-4">
          <p className="text-sm text-white/30">{t("pricing_secure")}</p>
          {!isWebView && user && currentPlan !== "descubre" && (
            <Button variant="ghost" onClick={() => setShowCancelModal(true)} className="text-white/30 hover:text-red-400 text-sm" data-testid="cancel-subscription-btn">
              {t("pricing_cancel")}
            </Button>
          )}
          <div className="flex items-center justify-center gap-6 text-sm">
            <Link to="/terminos" className="text-white/30 hover:text-white transition-colors">{t("footer_terms")}</Link>
            <Link to="/privacidad" className="text-white/30 hover:text-white transition-colors">{t("footer_privacy")}</Link>
          </div>
        </div>

        {/* Cancel subscription modal — WebView safe */}
        {showCancelModal && (
          <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/70 backdrop-blur-sm px-4" onClick={() => setShowCancelModal(false)} data-testid="cancel-modal-overlay">
            <div className="bg-[#1a1a2e] border border-white/10 rounded-2xl p-6 max-w-sm w-full" onClick={(e) => e.stopPropagation()}>
              <h3 className="font-['Outfit'] font-semibold text-lg mb-3">{t("pricing_cancel")}</h3>
              <p className="text-white/60 text-sm mb-6">{t("pricing_cancel_confirm")}</p>
              <div className="flex gap-3">
                <Button onClick={() => setShowCancelModal(false)} variant="outline" className="flex-1 rounded-xl border-white/10 text-white hover:bg-white/5 min-h-[48px]">
                  {t("history_delete_cancel")}
                </Button>
                <Button onClick={async () => {
                  try { await axios.post(`${API}/subscription/cancel`, {}, { withCredentials: true }); window.location.reload(); }
                  catch { setShowCancelModal(false); }
                }} className="flex-1 rounded-xl bg-red-500 hover:bg-red-600 text-white min-h-[48px]">
                  {t("pricing_cancel")}
                </Button>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default Pricing;
